/**
 * The signed-in session, kept in localStorage (protected by the strict CSP). The JWT
 * lasts 12 h, is bound to the login IP and cannot be refreshed, so an expired or
 * unreadable session is simply dropped.
 */
import { z } from 'zod';

export interface SessionUser {
  id: string;
  operatorId: string | null;
  email: string;
  fullName: string;
  permission: string;
  brand: string | null;
}

export interface Session {
  token: string;
  user: SessionUser;
  /** Epoch ms from the JWT `exp`, or null if the token carries none. */
  expiresAt: number | null;
}

export const SESSION_STORAGE_KEY = 'rgs.session';

const sessionSchema = z.object({
  token: z.string().min(1),
  user: z.object({
    id: z.string(),
    operatorId: z.string().nullable(),
    email: z.string(),
    fullName: z.string(),
    permission: z.string(),
    brand: z.string().nullable(),
  }),
  expiresAt: z.number().nullable(),
});

/** Reads `exp` without verifying the signature: the server does that on every request. */
export function decodeJwtExp(token: string): number | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const json = atob(
      part
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(part.length / 4) * 4, '='),
    );
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

export const isExpired = (s: Session, now = Date.now()) => s.expiresAt !== null && s.expiresAt <= now;

export function readSession(storage: Pick<Storage, 'getItem'> = localStorage, now = Date.now()): Session | null {
  try {
    const raw = storage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = sessionSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || isExpired(parsed.data, now)) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

export function writeSession(session: Session, storage: Pick<Storage, 'setItem'> = localStorage) {
  storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(storage: Pick<Storage, 'removeItem'> = localStorage) {
  storage.removeItem(SESSION_STORAGE_KEY);
}
